import React, { useState, useMemo } from 'react';
import { TransformationStrategy, PriorityLevel, GrowthLevel } from '../../types';

interface ArtOfPossibleTableProps {
    strategies: TransformationStrategy[];
    onPriorityChange: (id: string, value: PriorityLevel) => void;
    onGrowthChange: (id: string, value: GrowthLevel) => void;
    onRankChange?: (transformationArea: string, sourceIndex: number, destinationIndex: number) => void;
}

type SortField = 'transformationArea' | 'notes' | 'priority' | 'growth' | 'rank';
type SortDirection = 'asc' | 'desc';

const ArtOfPossibleTable: React.FC<ArtOfPossibleTableProps> = ({
    strategies,
    onPriorityChange,
    onGrowthChange,
    onRankChange,
}) => {
    const [draggedItem, setDraggedItem] = useState<{
        id: string;
        area: string;
        index: number;
    } | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [sortField, setSortField] = useState<SortField>('transformationArea');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            // Toggle direction if clicking the same field
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new field and default to ascending
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const toggleArea = (area: string) => {
        const newExpandedAreas = new Set(expandedAreas);
        if (newExpandedAreas.has(area)) {
            newExpandedAreas.delete(area);
        } else {
            newExpandedAreas.add(area);
        }
        setExpandedAreas(newExpandedAreas);
    };

    // Group strategies by transformation area first, then sort within groups
    const groupedStrategies = useMemo(() => {
        // First, group strategies by transformation area
        const areaGroups = strategies.reduce((acc, strategy) => {
            if (!acc[strategy.transformationArea]) {
                acc[strategy.transformationArea] = [];
            }
            acc[strategy.transformationArea].push(strategy);
            return acc;
        }, {} as Record<string, TransformationStrategy[]>);

        // Sort strategies within each area group
        Object.keys(areaGroups).forEach((area) => {
            areaGroups[area].sort((a, b) => {
                let comparison = 0;

                if (sortField === 'rank') {
                    // Numeric comparison for rank
                    const aRank = typeof a.rank === 'number' ? a.rank : 0;
                    const bRank = typeof b.rank === 'number' ? b.rank : 0;
                    comparison = aRank - bRank;
                } else if (sortField === 'priority') {
                    // Priority levels: High > Mid > Low > Select
                    const priorityOrder: Record<string, number> = {
                        'High': 1,
                        'Mid': 2,
                        'Low': 3,
                        'Select': 4
                    };
                    const aPriority = priorityOrder[a.priority] || 99;
                    const bPriority = priorityOrder[b.priority] || 99;
                    comparison = aPriority - bPriority;
                } else if (sortField === 'growth') {
                    // Growth levels: High > Mid > Low > Select
                    const growthOrder: Record<string, number> = {
                        'High': 1,
                        'Mid': 2,
                        'Low': 3,
                        'Select': 4
                    };
                    const aGrowth = growthOrder[a.growth] || 99;
                    const bGrowth = growthOrder[b.growth] || 99;
                    comparison = aGrowth - bGrowth;
                } else if (sortField === 'notes') {
                    // String comparison for notes/effort
                    const aValue = a.notes || '';
                    const bValue = b.notes || '';
                    comparison = aValue.localeCompare(bValue);
                } else if (sortField === 'transformationArea') {
                    // String comparison for transformation area
                    const aValue = a.transformationArea || '';
                    const bValue = b.transformationArea || '';
                    comparison = aValue.localeCompare(bValue);
                }

                // Apply sort direction
                return sortDirection === 'asc' ? comparison : -comparison;
            });
        });

        // If sorting by transformationArea, also sort the area groups themselves
        if (sortField === 'transformationArea') {
            const sortedAreaKeys = Object.keys(areaGroups).sort((a, b) => {
                return sortDirection === 'asc' 
                    ? a.localeCompare(b)
                    : b.localeCompare(a);
            });

            const sortedGroups: Record<string, TransformationStrategy[]> = {};
            sortedAreaKeys.forEach(key => {
                sortedGroups[key] = areaGroups[key];
            });
            return sortedGroups;
        }

        return areaGroups;
    }, [strategies, sortField, sortDirection]);

    // Get background color based on priority or growth level
    const getLevelBgColor = (level: PriorityLevel | GrowthLevel) => {
        switch (level) {
            case 'High':
                return 'bg-[#FFEFF0]';
            case 'Mid':
                return 'bg-[#FFF7E6]';
            case 'Low':
                return 'bg-[#EAF6FB]';
            default:
                return 'bg-transparent';
        }
    };

    // Drag and drop handlers
    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, id: string, area: string, index: number) => {
        setDraggedItem({ id, area, index });
        e.currentTarget.classList.add('opacity-75', 'shadow-lg', 'bg-gray-50');
        
        try {
            e.dataTransfer.setData('text/plain', id);
            e.dataTransfer.effectAllowed = 'move';
        } catch (err) {
            console.error('Error setting drag data:', err);
        }
    };

    const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
        e.currentTarget.classList.remove('opacity-75', 'shadow-lg', 'bg-gray-50');
        setDraggedItem(null);
    };

    const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>, area: string, index: number) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent<HTMLTableRowElement>, area: string, index: number) => {
        e.preventDefault();
        setDragOverIndex(null);
        
        if (draggedItem && onRankChange) {
            onRankChange(area, draggedItem.index, index);
        }
    };

    // Helper to render sort indicator
    const renderSortIndicator = (field: SortField) => {
        if (sortField !== field) {
            return (
                <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12L6 17L11 12M1 6L6 1L11 6" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        }

        return sortDirection === 'asc' ? (
            <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 6L6 1L11 6" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ) : (
            <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 12L6 17L11 12" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
    };

    return (
        <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-transparent border">
                <thead>
                    <tr className="bg-transparent/5">
                        <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F] w-1/5">
                            <div 
                                className="flex justify-between items-center cursor-pointer" 
                                onClick={() => handleSort('transformationArea')}
                            >
                                Transformation area
                                {renderSortIndicator('transformationArea')}
                            </div>
                        </th>
                        <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F] w-2/5">
                            <div 
                                className="flex justify-between items-center cursor-pointer" 
                                onClick={() => handleSort('notes')}
                            >
                                Effort
                                {renderSortIndicator('notes')}
                            </div>
                        </th>
                        <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F] w-1/5">
                            <div 
                                className="flex justify-between items-center cursor-pointer" 
                                onClick={() => handleSort('priority')}
                            >
                                Business Impact
                                {renderSortIndicator('priority')}
                            </div>
                        </th>
                        <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F] w-1/5">
                            <div 
                                className="flex justify-between items-center cursor-pointer" 
                                onClick={() => handleSort('growth')}
                            >
                                Technical Impact
                                {renderSortIndicator('growth')}
                            </div>
                        </th>
                        <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F] w-1/5">
                            <div 
                                className="flex justify-between items-center cursor-pointer" 
                                onClick={() => handleSort('rank')}
                            >
                                Rank
                                {renderSortIndicator('rank')}
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody className='text-sm'>
                    {Object.entries(groupedStrategies).map(([area, areaStrategies]) => {
                        const isExpanded = expandedAreas.has(area);
                        
                        return (
                            <React.Fragment key={area}>
                                {/* Area Header Row - Always Visible */}
                                <tr 
                                    className={`border-b border-gray-200 cursor-pointer transition-all duration-200 ${isExpanded ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                                    onClick={() => toggleArea(area)}
                                >
                                    <td className="p-3 text-[#0F2A4F] font-medium italic align-top border-r">
                                        <div className="flex items-center gap-2">
                                            <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </div>
                                            <span>{area}</span>
                                        </div>
                                    </td>
                                    <td colSpan={4} className="p-3 text-[#0F2A4F] font-medium italic text-center">
                                        {isExpanded ? 'Click to collapse' : 'Click to expand'}
                                    </td>
                                </tr>
                                
                                                                        {/* Efforts Rows - Only Visible When Expanded */}
                                        {isExpanded && areaStrategies.map((strategy, index) => (
                                            <tr 
                                                key={strategy.id} 
                                                className={`border-b border-gray-200 bg-gray-50 transition-all duration-300 slide-down-animation ${dragOverIndex === index && draggedItem?.area === area ? 'bg-blue-50' : ''}`}
                                                onDragOver={(e) => handleDragOver(e, area, index)}
                                                onDragLeave={handleDragLeave}
                                                onDrop={(e) => handleDrop(e, area, index)}
                                            >
                                        <td className="p-3 text-[#0F2A4F] font-medium italic align-top border-r">
                                            {/* Empty cell for transformation area column when showing efforts */}
                                        </td>
                                        <td className="p-2 text-[#0F2A4F]">
                                            <div 
                                                className="flex items-center w-full cursor-grab active:cursor-grabbing transition-all duration-200 hover:bg-gray-50 rounded p-1"
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, strategy.id, area, index)}
                                                onDragEnd={handleDragEnd}
                                                title="Drag to reorder"
                                            >
                                                <div className="inline-flex items-center justify-center">
                                                    <span className="text-gray-400 mr-2 select-none text-xl font-bold transition-colors duration-200 pb-0.5 hover:text-gray-600 align-middle">≡</span>
                                                    <span className="align-middle">{strategy.notes}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className={`p-3 ${getLevelBgColor(strategy.priority)}`}>
                                            <div className="relative">
                                                <select
                                                    value={strategy.priority}
                                                    onChange={(e) => onPriorityChange(strategy.id, e.target.value as PriorityLevel)}
                                                    className="appearance-none w-full bg-transparent border-none p-1 pr-8 focus:outline-none text-[#0F2A4F]"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <option value="Select">Select</option>
                                                    <option value="High">High</option>
                                                    <option value="Mid">Mid</option>
                                                    <option value="Low">Low</option>
                                                </select>
                                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-[#0F2A4F]">
                                                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </td>
                                        <td className={`p-3 ${getLevelBgColor(strategy.growth)}`}>
                                            <div className="relative">
                                                <select
                                                    value={strategy.growth}
                                                    onChange={(e) => onGrowthChange(strategy.id, e.target.value as GrowthLevel)}
                                                    className="appearance-none w-full bg-transparent border-none p-1 pr-8 focus:outline-none text-[#0F2A4F]"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <option value="Select">Select</option>
                                                    <option value="High">High</option>
                                                    <option value="Mid">Mid</option>
                                                    <option value="Low">Low</option>
                                                </select>
                                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-[#0F2A4F]">
                                                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3 text-[#0F2A4F] text-center">{strategy.rank}</td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ArtOfPossibleTable;
